import React, { Children, memo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { BlueprintGrid } from '../components/BlueprintGrid';
import { TopographicPattern } from '../components/TopographicPattern';
export function WhereAIFits() {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <main className="w-full" data-id="element-922">
      {/* Hero */}
      <section className="relative py-20 px-6 bg-paper pt-32 overflow-hidden" data-id="element-923">
        <div className="absolute inset-0 text-seaglass/[0.15]" data-id="element-924">
          <BlueprintGrid data-id="element-925" />
        </div>

        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-926">
          <motion.h1 variants={fadeIn} className="text-4xl md:text-6xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-927">
            AI is the answer. But probably not to the question you are asking.
          </motion.h1>

          <motion.div variants={fadeIn} className="text-lg text-text max-w-2xl mb-8 space-y-4" data-id="element-928">
            <p data-id="element-929">
              AI does not fix structural drag. It amplifies whatever is already
              there. If the business still depends on tribal knowledge, vague
              rules, and constant exception-handling, AI usually creates faster
              confusion, not real leverage.
            </p>
            <p className="text-xl text-navy font-medium" data-id="element-930">
              Technology follows clarity. We do not automate confusion.
            </p>
          </motion.div>

          {/* Hero CTAs - Side by side on desktop, stacked on mobile */}
          <motion.div variants={fadeIn} className="flex flex-col sm:flex-row sm:items-start gap-6 mb-10" data-id="element-931">
            {/* Primary CTA with microcopy */}
            <div className="flex flex-col" data-id="element-932">
              <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-933">
                <span data-id="element-934">Book an Honest Conversation</span>
                <ArrowRight className="w-5 h-5" data-id="element-935" />
              </Link>
              <motion.p variants={fadeIn} className="text-sm text-text-muted mt-2" data-id="element-936">
                45 minutes. No pitch. Just clarity.
              </motion.p>
            </div>

            {/* Secondary link with microcopy */}
            <div className="flex flex-col" data-id="element-937">
              <a href="#ai-ready" className="inline-flex items-center space-x-2 px-6 py-4 border border-navy/20 hover:border-navy/40 text-navy/70 hover:text-navy font-medium transition-all duration-300" data-id="element-938">
                <span data-id="element-939">What AI-ready actually means</span>
                <ChevronDown className="w-4 h-4" data-id="element-940" />
              </a>
              <motion.p variants={fadeIn} className="text-sm text-text-muted mt-2" data-id="element-941">
                See the prerequisites below
              </motion.p>
            </div>
          </motion.div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center" animate={{
        y: [0, 6, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-942">
          <ChevronDown className="w-5 h-5 text-seaglass/50" data-id="element-943" />
        </motion.div>
      </section>

      {/* Why most AI efforts stall */}
      <section className="py-24 px-6 border-t hairline-top bg-paper-dark" data-id="element-944">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-945">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-946">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-947">
              Why most AI efforts stall
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-948">
            <p className="text-text" data-id="element-949">
              Most companies do not have an AI problem first.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-950">
              They have a clarity problem first.
            </p>
            <p className="text-text" data-id="element-951">
              The logic is still in people's heads. The rules are inconsistent.
              The handoffs are fuzzy. The definitions drift. The exception path
              is where the real work gets done.
            </p>
            <p className="text-text" data-id="element-952">
              In that environment, AI does not know what good means.
            </p>
            <p className="text-text" data-id="element-953">
              It can summarize noise. It can speed up bad inputs. It can create
              the appearance of progress.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-954">
              But it cannot create operating clarity where none exists.
            </p>
            <p className="text-text mt-2" data-id="element-955">
              Most process is compensating for missing decision logic. AI
              inherits that same gap.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* What "AI-ready" actually means */}
      <section id="ai-ready" className="relative py-24 px-6 bg-seaglass/5 grain border-y border-seaglass/20 overflow-hidden" data-id="element-956">
        <TopographicPattern color="sage" opacity={0.06} data-id="element-957" />

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-958">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-959">
            <p className="eyebrow mb-4 text-seaglass" data-id="element-960">Prerequisites</p>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-961">
              What &quot;AI-ready&quot; actually means
            </h2>
            <p className="text-lg text-text mb-4" data-id="element-962">
              AI-ready does not mean buying a tool. It means the business has
              enough clarity for software to assist without increasing
              confusion. That usually requires:
            </p>
          </motion.div>

          <motion.div variants={fadeIn} className="mb-10" data-id="element-963">
            <div className="space-y-0" data-id="element-964">
              {[{
              num: '1',
              title: 'Definitions',
              desc: 'Do the words, stages, statuses, and metrics mean the same thing across the business?'
            }, {
              num: '2',
              title: 'Decision logic',
              desc: 'Are the rules clear enough that the system can follow them consistently?'
            }, {
              num: '3',
              title: 'Ownership',
              desc: 'Does someone actually own the output, exceptions, and edge cases?'
            }, {
              num: '4',
              title: 'Externalized knowledge',
              desc: 'Is the logic living somewhere usable, or is it still trapped in memory and scattered conversations?'
            }, {
              num: '5',
              title: 'Guardrails',
              desc: 'Is it clear where AI can assist, where a human must decide, and what "good" actually looks like?'
            }].map(item => <div key={item.num} className="py-6 border-b border-seaglass/20 last:border-0 flex items-start space-x-6" data-id="element-965">
                  <span className="text-xs font-mono font-semibold text-seaglass uppercase tracking-wider flex-shrink-0 mt-1 w-4" data-id="element-966">
                    {item.num}
                  </span>
                  <div data-id="element-967">
                    <h3 className="text-xl font-semibold text-navy mb-2" data-id="element-968">
                      {item.title}
                    </h3>
                    <p className="text-base text-text" data-id="element-969">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </motion.div>

          {/* Hard truth */}
          <motion.div variants={fadeIn} data-id="element-970">
            <div className="border-l-2 border-seaglass pl-8 py-2" data-id="element-971">
              <p className="text-body text-text font-medium" data-id="element-972">
                If those are missing, the right answer is usually not &quot;more
                AI.&quot; It is better structure.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* What this looks like in practice */}
      <section className="py-24 px-6 bg-paper" data-id="element-973">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-974">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-975">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-976">
              What this looks like in practice
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-977">
            <p className="text-body text-text" data-id="element-978">
              A founder had his team build a service ticket platform to improve
              the customer experience and asked us to look at it.
            </p>

            <p className="text-body text-text" data-id="element-979">
              The UI was good. But the bigger win was what the system made
              visible.
            </p>

            <p className="text-body text-text" data-id="element-980">
              The gold mine was in the service data. We lined it up with account
              data so that the business could finally see which requests were
              coming back within 30 days of onboarding, where installation
              quality was breaking down upstream, and which industries were
              consuming the most service time relative to the revenue they
              produced.
            </p>

            <p className="text-body text-navy font-semibold" data-id="element-981">
              That was the real leverage.
            </p>

            <p className="text-body text-text" data-id="element-982">
              The platform did not just improve the workflow. It created trusted
              signals the business could act on.
            </p>

            <div className="py-8 my-4 border-y hairline-top hairline-bottom space-y-4" data-id="element-983">
              <p className="text-body text-text" data-id="element-984">
                Only after that did AI become useful in the next layer.
              </p>
              <p className="text-body text-text" data-id="element-985">
                With cleaner workflow truth in place, we built a custom GPT for
                field service agents using the equipment manuals as reference
                material so they could troubleshoot faster from their phones.
              </p>
            </div>

            <p className="text-body text-navy font-semibold" data-id="element-986">
              That is how AI should work.
            </p>

            <p className="text-body text-text" data-id="element-987">
              Not as a substitute for clarity.
            </p>

            <p className="text-body text-text font-medium" data-id="element-988">
              As a force multiplier on top of it.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Where AI creates real leverage */}
      <section className="py-24 px-6 bg-paper-dark grain" data-id="element-989">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-990">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-991">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-992">
              Where AI creates real leverage
            </h2>
            <p className="text-lg text-text" data-id="element-993">
              Once the structure is clear, AI can help in useful ways:
            </p>
          </motion.div>

          <motion.div variants={fadeIn} className="mb-10" data-id="element-994">
            <ul className="space-y-3" data-id="element-995">
              {['summarizing and routing cleanly structured information', 'drafting within defined rules', 'speeding up repetitive internal workflows', 'supporting service and support teams with consistent context', 'helping teams work faster without creating more ambiguity'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-996">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-997" />
                  <span className="text-body text-text" data-id="element-998">{item}</span>
                </li>)}
            </ul>
          </motion.div>

          <motion.div variants={fadeIn} data-id="element-999">
            <div className="border-l-2 border-seaglass pl-8 py-2" data-id="element-1000">
              <p className="text-body text-text font-medium" data-id="element-1001">
                The point is not to force AI into the business. The point is to
                make sure it is accelerating something real.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* What not to do */}
      <section className="py-24 px-6 bg-paper" data-id="element-1002">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-1003">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-1004">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-1005">
              What not to do
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6 text-body" data-id="element-1006">
            <ul className="space-y-3" data-id="element-1007">
              {['Do not ask AI to fix a business that still runs on unclear rules', 'Do not try to automate a workflow nobody can define cleanly', 'Do not mistake tool activity for operating leverage', 'Do not hand a model the job of interpreting what the business itself has not clarified yet'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-1008">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-1009" />
                  <span className="text-text" data-id="element-1010">{item}</span>
                </li>)}
            </ul>
            <p className="text-text font-medium text-navy" data-id="element-1011">
              Fix the recurring failure first. Then decide what to automate.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Dark CTA Band */}
      <section className="relative py-24 px-6 bg-navy grain overflow-hidden" data-id="element-1012">
        <div className="absolute inset-0 text-white/[0.05]" data-id="element-1013">
          <BlueprintGrid data-id="element-1014" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="relative max-w-3xl mx-auto text-center z-10" data-id="element-1015">
          <h2 className="text-4xl md:text-5xl font-serif font-semibold text-white mb-6 leading-tight" data-id="element-1016">
            If AI is not helping yet, the problem may not be AI.
          </h2>

          <div className="space-y-2 mb-10" data-id="element-1017">
            <p className="text-lg text-white/80" data-id="element-1018">
              It may be that the business logic is still too unclear to
              automate. That is a structural issue first.
            </p>
          </div>

          <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300 group" data-id="element-1019">
            <span data-id="element-1020">Book an Honest Conversation</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" data-id="element-1021" />
          </Link>
          <p className="text-sm text-white/50 mt-4" data-id="element-1022">
            45 minutes. No pitch. Just clarity.
          </p>
        </motion.div>
      </section>
    </main>;
}